import React, { useState, useEffect, useRef } from 'react';
import logo from './home/logo.png';
import logo512 from './home/logo512.png';

const productLinks = [
	{ href: '/products', label: 'All Products' },
	{ href: '/products/carpet', label: 'Carpet' },
	{ href: '/products/hardwood', label: 'Hardwood' },
	{ href: '/products/naturalstone', label: 'Natural Stone' },
	{ href: '/products/Tile', label: 'Tile' },
	{ href: '/products/luxuryvinyl', label: 'Luxury Vinyl' },
	{ href: '/products/vinyl', label: 'Vinyl' },
	{ href: '/products/glasstile', label: 'Glass Tile' },
	{ href: '/products/metaltile', label: 'Metal Tile' },
];

const serviceLinks = [
	{ href: '/flooring-services', label: 'All Services' },
	{ href: '/kitchen-bath-showroom', label: 'Kitchen & Bath Showroom' },
	{ href: '/bathroom-remodeling', label: 'Bathroom Remodeling' },
	{ href: '/blinds-n-shutters', label: 'Blinds & Shutters' },
	{ href: '/builder-n-contractor-program', label: 'Builder & Contractor Program' },
	{ href: '/design-services', label: 'Design Services' },
];

const mainLinks = [
	{ href: '/reviews', label: 'Reviews' },
	{ href: '/financing', label: 'Financing' },
	{ href: '/inspiration', label: 'Inspiration' },
	{ href: '/about-us', label: 'About' },
	{ href: '/contact', label: 'Contact' },
];

const navItemClass =
	'inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-[#803333] hover:bg-[#803333]/5 transition-colors no-underline border-0 bg-transparent cursor-pointer';

const dropdownItemClass =
	'flex items-center w-full px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-[#803333] hover:bg-[#803333]/5 no-underline transition-colors';

function NavDropdown({ label, links, open, onToggle, onClose }) {
	const ref = useRef(null);

	useEffect(() => {
		if (!open) return undefined;
		const onClickOutside = (event) => {
			if (ref.current && !ref.current.contains(event.target)) {
				onClose();
			}
		};
		const onEscape = (event) => {
			if (event.key === 'Escape') onClose();
		};
		document.addEventListener('mousedown', onClickOutside);
		document.addEventListener('keydown', onEscape);
		return () => {
			document.removeEventListener('mousedown', onClickOutside);
			document.removeEventListener('keydown', onEscape);
		};
	}, [open, onClose]);

	return (
		<div className="relative" ref={ref}>
			<button
				type="button"
				className={`${navItemClass} ${open ? 'text-[#803333] bg-[#803333]/5' : ''}`}
				aria-expanded={open}
				aria-haspopup="true"
				onClick={onToggle}
			>
				{label}
				<svg
					className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					aria-hidden="true"
				>
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
				</svg>
			</button>
			{open && (
				<div className="absolute left-0 top-full mt-2 min-w-[240px] rounded-xl border border-gray-100 bg-white py-2 shadow-xl z-50">
					{links.map((link) => (
						<a
							key={link.href}
							href={link.href}
							className={dropdownItemClass}
							onClick={onClose}
						>
							{link.label}
						</a>
					))}
				</div>
			)}
		</div>
	);
}

function Header() {
	const [menuOpen, setMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const [openDropdown, setOpenDropdown] = useState(null);
	const [mobileSection, setMobileSection] = useState(null);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		document.body.style.overflow = menuOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [menuOpen]);

	const closeMenu = () => {
		setMenuOpen(false);
		setOpenDropdown(null);
		setMobileSection(null);
	};

	return (
		<header className="font-sans sticky top-0 z-50">
			{/* Utility bar */}
			<div className="bg-[#803333] text-white">
				<div className="container mx-auto px-4 flex items-center justify-between gap-3 py-2 text-xs sm:text-sm">
					<a
						href="https://www.google.com/maps/place/Nickle's+Flooring/@36.6728458,-93.8654706,17z"
						target="_blank"
						rel="noreferrer"
						className="hidden sm:inline-flex items-center gap-1.5 text-white/90 hover:text-white no-underline transition-colors truncate"
					>
						<i className="bi bi-geo-alt-fill" />
						<span className="truncate">470 State Highway 76, Cassville, MO 65625</span>
					</a>
					<a
						href="tel:+14178472484"
						className="inline-flex items-center gap-1.5 text-white font-semibold no-underline hover:text-white/90 transition-colors ms-auto sm:ms-0"
					>
						<i className="bi bi-telephone-fill" />
						(417) 847-2484
					</a>
				</div>
			</div>

			{/* Main bar */}
			<div
				className={`bg-white/95 backdrop-blur-md border-b transition-shadow ${
					scrolled ? 'shadow-md border-gray-200' : 'shadow-sm border-gray-100'
				}`}
			>
				<div className="container mx-auto px-4">
					<div className="flex items-center justify-between gap-4 h-16 md:h-[72px]">
						<a href="/" className="flex items-center gap-2.5 no-underline flex-shrink-0 min-w-0">
							<img
								src={logo512}
								alt=""
								className="hidden sm:block h-11 w-11 object-contain"
							/>
							<img
								src={logo}
								alt="Nickle's Flooring"
								className="h-9 sm:h-10 md:h-11 w-auto max-w-[160px] sm:max-w-[200px] object-contain"
							/>
						</a>

						{/* Desktop nav — shared button style */}
						<nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
							<NavDropdown
								label="Products"
								links={productLinks}
								open={openDropdown === 'products'}
								onToggle={() =>
									setOpenDropdown((current) => (current === 'products' ? null : 'products'))
								}
								onClose={() => setOpenDropdown(null)}
							/>
							<NavDropdown
								label="Services"
								links={serviceLinks}
								open={openDropdown === 'services'}
								onToggle={() =>
									setOpenDropdown((current) => (current === 'services' ? null : 'services'))
								}
								onClose={() => setOpenDropdown(null)}
							/>
							{mainLinks.map((link) => (
								<a key={link.href} href={link.href} className={navItemClass}>
									{link.label}
								</a>
							))}
						</nav>

						<div className="flex items-center gap-2 flex-shrink-0">
							<a
								href="/free-estimate"
								className="hidden md:inline-flex items-center justify-center h-10 px-4 rounded-lg bg-[#803333] text-white text-sm font-semibold no-underline hover:bg-[#6a2a2a] transition-colors shadow-sm"
							>
								Free Estimate
							</a>

							<a
								href="tel:+14178472484"
								className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-[#803333] hover:bg-[#803333]/10 no-underline transition-colors"
								aria-label="Call us"
							>
								<i className="bi bi-telephone-fill" />
							</a>

							<button
								type="button"
								onClick={() => setMenuOpen((open) => !open)}
								className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-800 hover:bg-[#803333]/5 hover:text-[#803333] border-0 bg-transparent transition-colors"
								aria-label={menuOpen ? 'Close menu' : 'Open menu'}
								aria-expanded={menuOpen}
							>
								{menuOpen ? (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								) : (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
									</svg>
								)}
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile menu */}
			{menuOpen && (
				<div className="lg:hidden fixed inset-0 top-[calc(2.25rem+4rem)] z-[60] flex flex-col">
					<nav
						className="relative z-20 bg-white shadow-xl max-h-[75vh] overflow-y-auto border-t border-gray-100"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="container mx-auto px-4 py-4 space-y-2">
							{[
								{ key: 'products', label: 'Products', links: productLinks },
								{ key: 'services', label: 'Services', links: serviceLinks },
							].map((section) => {
								const isOpen = mobileSection === section.key;
								return (
									<div
										key={section.key}
										className="rounded-xl border border-gray-100 overflow-hidden bg-white"
									>
										<button
											type="button"
											className={`flex w-full items-center justify-between px-4 h-12 text-sm font-semibold border-0 transition-colors ${
												isOpen
													? 'bg-[#803333]/5 text-[#803333]'
													: 'bg-white text-gray-800 hover:bg-[#803333]/5 hover:text-[#803333]'
											}`}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setMobileSection((current) =>
													current === section.key ? null : section.key
												);
											}}
										>
											{section.label}
											<svg
												className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
											</svg>
										</button>
										{isOpen && (
											<div className="border-t border-gray-100 bg-gray-50 py-1 relative z-30">
												{section.links.map((link) => (
													<a
														key={link.href}
														href={link.href}
														className={`${dropdownItemClass} relative z-30`}
														onClick={(e) => {
															e.stopPropagation();
															closeMenu();
														}}
													>
														{link.label}
													</a>
												))}
											</div>
										)}
									</div>
								);
							})}

							{mainLinks.map((link) => (
								<a
									key={link.href}
									href={link.href}
									onClick={(e) => {
										e.stopPropagation();
										closeMenu();
									}}
									className="flex items-center h-12 px-4 rounded-xl text-sm font-semibold text-gray-800 hover:bg-[#803333]/5 hover:text-[#803333] no-underline transition-colors"
								>
									{link.label}
								</a>
							))}

							<a
								href="/free-estimate"
								onClick={(e) => {
									e.stopPropagation();
									closeMenu();
								}}
								className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-[#803333] text-white text-sm font-semibold no-underline hover:bg-[#6a2a2a] transition-colors mt-2"
							>
								Request an Estimate
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</a>

							<a
								href="https://www.google.com/maps/place/Nickle's+Flooring/@36.6728458,-93.8654706,17z"
								target="_blank"
								rel="noreferrer"
								onClick={(e) => {
									e.stopPropagation();
									closeMenu();
								}}
								className="flex items-start gap-2 px-4 py-3 text-xs text-gray-500 no-underline sm:hidden"
							>
								<i className="bi bi-geo-alt-fill text-[#803333] mt-0.5" />
								470 State Highway 76, Cassville, MO 65625
							</a>
						</div>
					</nav>

					{/* Backdrop below the menu panel so links stay clickable */}
					<button
						type="button"
						className="relative z-10 flex-1 w-full bg-black/40 border-0 cursor-pointer"
						aria-label="Close menu"
						onClick={closeMenu}
					/>
				</div>
			)}
		</header>
	);
}

export default Header;
